import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/composite/gaussian-blur/definition"
import {ImageOpNodeResize} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/resize/node"
import {computeKernel, MAX_HALF_KERNEL_SIZE} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/composite/gaussian-blur/helpers"
import {InterpolationMode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/resize-helper-hal"
import {Evaluator, registerNode} from "@cm/graph-v2-final"
import {
    AnyImageOpContext,
    AnyImageOpResultNode,
    ImageOpOutputs,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {CompositeImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/composite-image-op-node"
import {ImageOpNodeConvolve} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/convolve/node"

@registerNode
export class ImageOpNodeGaussianBlur extends CompositeImageOpNode<typeof definition> {
    static nodeClass = "ImageOpNodeGaussianBlur"
    static definition = definition

    override async execute(evaluator: Evaluator<AnyImageOpContext>, imageOpNodeBag: ImageOpNodeBag): Promise<ImageOpOutputs<typeof definition>> {
        const {sourceImage, sigma: sigmaIn, kernelClampThresh, borderMode} = await evaluator.getAll(this.parameters)
        let sigma = sigmaIn
        if (typeof sigma === "number") {
            sigma = {x: sigma, y: sigma}
        }
        if (sigma.x === 0 && sigma.y === 0) {
            // no need to blur
            return sourceImage
        }

        const computeHalfKernelSize = (sigma: number, yThresh: number) => Math.sqrt(2) * Math.sqrt(sigma * sigma * Math.log(1 / yThresh))
        const halfKernelSize = {
            x: Math.ceil(computeHalfKernelSize(sigma.x, kernelClampThresh)),
            y: Math.ceil(computeHalfKernelSize(sigma.y, kernelClampThresh)),
        }
        const computeDownSamplingFactor = (imageSize: number, halfKernelSize: number) =>
            Math.floor(imageSize * Math.min(1, MAX_HALF_KERNEL_SIZE / halfKernelSize)) / imageSize
        const downSamplingFactor = {
            x: computeDownSamplingFactor(sourceImage.descriptor.width, halfKernelSize.x),
            y: computeDownSamplingFactor(sourceImage.descriptor.height, halfKernelSize.y),
        }

        const interpolationMode: InterpolationMode = "cubic"
        const resizeAddressMode = borderMode === "wrap" ? "wrap" : "clamp"
        const downSampledSourceImage = imageOpNodeBag.getNode("preResize", ImageOpNodeResize, {
            sourceImage: sourceImage,
            interpolationMode: interpolationMode,
            resultSize: {
                width: sigma.x === 0 ? 1 : Math.round(sourceImage.descriptor.width * downSamplingFactor.x),
                height: sigma.y === 0 ? 1 : Math.round(sourceImage.descriptor.height * downSamplingFactor.y),
            },
            addressMode: resizeAddressMode,
        })

        let blurredImageH: AnyImageOpResultNode
        if (sigma.x > 0) {
            blurredImageH = imageOpNodeBag.getNode("convolveH", ImageOpNodeConvolve, {
                sourceImage: downSampledSourceImage,
                kernel: (() => {
                    const halfKernelSizeH = Math.ceil(halfKernelSize.x * downSamplingFactor.x)
                    const sigmaH = sigma.x * downSamplingFactor.x
                    const kernelH = computeKernel(halfKernelSizeH, sigmaH)
                    return {
                        width: kernelH.length,
                        height: 1,
                        values: kernelH,
                    }
                })(),
                borderMode,
            })
        } else {
            blurredImageH = downSampledSourceImage
        }

        let blurredImage: AnyImageOpResultNode
        if (sigma.y > 0) {
            blurredImage = imageOpNodeBag.getNode("convolveV", ImageOpNodeConvolve, {
                sourceImage: blurredImageH,
                kernel: (() => {
                    const halfKernelSizeV = Math.ceil(halfKernelSize.y * downSamplingFactor.y)
                    const sigmaV = sigma.y * downSamplingFactor.y
                    const kernelV = computeKernel(halfKernelSizeV, sigmaV)
                    return {
                        width: 1,
                        height: kernelV.length,
                        values: kernelV,
                    }
                })(),
                borderMode,
            })
        } else {
            blurredImage = blurredImageH
        }

        const postResize = imageOpNodeBag.getNode("postResize", ImageOpNodeResize, {
            sourceImage: blurredImage,
            interpolationMode: interpolationMode,
            resultSize: sourceImage.descriptor,
            addressMode: resizeAddressMode,
        })

        return await evaluator.get(postResize)
    }
}
